import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './VirtualAccountGuide.css';

function VirtualAccountGuide() {
    const navigate = useNavigate();
    const location = useLocation();
    const [showModal, setShowModal] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [showCheckbox, setShowCheckbox] = useState(false);
    const [loading, setLoading] = useState(false);
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const guideContentRef = useRef(null);
    const logoImageRef = useRef(null);

    // 텍스트 배열 - 여기에 문구만 추가하면 자동으로 적용됨
    const fullTexts = [
        '1회용 가상 계좌 발급을 위해 본인 및 계좌 인증을 진행해 주세요.',
        '보이스피싱이나 도박 자금 등 불법적인 목적으로 이용할 경우, 관련 법령에 따라 형사 처벌을 받습니다. 금융 사기 피해가 의심되는 경우 즉시 경찰청(112) 또는 금융감독원(1332)으로 신고하시기 바랍니다.',
        '위 내용을 모두 확인했습니다.'
    ];

    // 체크박스 상태 배열 초기화
    const [checkedItems, setCheckedItems] = useState(() => 
        new Array(fullTexts.length).fill(false)
    );

    // 이미지 로드 완료 감지
    useEffect(() => {
        const logoImg = logoImageRef.current;
        if (!logoImg) return;

        const handleImageLoad = () => {
            setImagesLoaded(true);
        };

        // 이미 이미 로드된 경우
        if (logoImg.complete && logoImg.naturalHeight !== 0) {
            setImagesLoaded(true);
        } else {
            logoImg.addEventListener('load', handleImageLoad);
            logoImg.addEventListener('error', handleImageLoad); // 에러 발생 시에도 표시
        }

        return () => {
            logoImg.removeEventListener('load', handleImageLoad);
            logoImg.removeEventListener('error', handleImageLoad);
        };
    }, []);

    // 스크롤 감지: .guide-content의 하단이 화면에 보일 때 체크박스 표시
    useEffect(() => {
        const checkScrollPosition = () => {
            const contentElement = guideContentRef.current;
            if (!contentElement) return;

            const contentRect = contentElement.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const documentHeight = document.documentElement.scrollHeight;

            // 스크롤이 필요한지 확인
            const hasScroll = documentHeight > viewportHeight + 20;

            if (!hasScroll) {
                // 스크롤이 필요 없으면 .guide-content의 하단이 화면에 보이는지 확인
                const isBottomVisible = contentRect.bottom <= viewportHeight + 10;
                setShowCheckbox(isBottomVisible);
            } else {
                // 스크롤이 필요하면 스크롤을 끝까지 내렸을 때만 표시
                const isScrolledToBottom = scrollTop + viewportHeight >= documentHeight - 10;
                setShowCheckbox(isScrolledToBottom);
            }
        };

        // 초기 체크
        setTimeout(() => {
            checkScrollPosition();
        }, 100);

        // 스크롤 이벤트 리스너
        window.addEventListener('scroll', checkScrollPosition);
        window.addEventListener('resize', checkScrollPosition);

        return () => {
            window.removeEventListener('scroll', checkScrollPosition);
            window.removeEventListener('resize', checkScrollPosition);
        };
    }, []);

    const handleNext = () => {
        // 모든 항목이 체크되어야 진행 가능
        if (checkedItems.length > 0 && checkedItems.every(checked => checked)) {
            setShowModal(true);
        }
    };

    const handleConfirm = () => {
        setLoading(true);
        setTimeout(() => {
            setShowModal(false);
            setLoading(false);
            navigate(`/issue${location.search || ''}`);
        }, 1000);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handlePoliceReport = () => {
        window.open('tel:112', '_self');
    };

    const handleFinancialReport = () => {
        window.open('tel:1332', '_self');
    };

    const handleOpenReportModal = () => {
        setShowReportModal(true);
    };

    const handleCloseReportModal = () => {
        setShowReportModal(false);
    };

    return (
        <div className="guide-container">
            {/* Top Section: Header + Main Text + Logo */}
            <div className="guide-top-section">
                <div className="guide-header">
                    <h1 className="guide-title">
                        <span className="highlight-text">(해피몰)</span> 1회용 입금 가상 계좌
                    </h1>
                </div>

                <div className="guide-main-text">
                    <h2>수수료 없는 현금결제</h2>
                </div>

                <div className="guide-logo">
                    <img 
                        ref={logoImageRef}
                        src="/img/ntoss_logo.png" 
                        alt="ntoss logo"
                    />
                </div>
            </div>

            {/* Main Content */}
            <div className="guide-content" ref={guideContentRef}>
                <div className="guide-info">
                    <ul>
                        {fullTexts.map((fullText, index) => {
                            const allPrevChecked = index === 0 || checkedItems.slice(0, index).every(checked => checked);
                            const isSecondLastItem = index === fullTexts.length - 2;

                            return (
                                <li 
                                    key={index}
                                    className="guide-info-item"
                                >
                                    <label className={`guide-checkbox-label ${index === 0 || allPrevChecked ? '' : 'disabled'}`}>
                                        <input
                                            type="checkbox"
                                            checked={checkedItems[index] || false}
                                            onChange={(e) => {
                                                if (index === 0 || allPrevChecked) {
                                                    const newCheckedItems = [...checkedItems];
                                                    newCheckedItems[index] = e.target.checked;
                                                    setCheckedItems(newCheckedItems);
                                                }
                                            }}
                                            disabled={index > 0 && !allPrevChecked}
                                        />
                                        <span className="guide-checkmark"></span>
                                    </label>
                                    <span className="guide-info-text">
                                        {isSecondLastItem ? (
                                            <>
                                                {fullText.replace('신고하시기 바랍니다.', '').trim()}
                                                {' '}
                                                <button 
                                                    className="report-link-text" 
                                                    onClick={handleOpenReportModal}
                                                >
                                                    신고
                                                </button>
                                                하시기 바랍니다.
                                            </>
                                        ) : (
                                            fullText
                                        )}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>

            {/* Footer Button */}
            <div className="guide-footer">
                <button
                    className={`guide-next-button ${checkedItems.length > 0 && checkedItems.every(checked => checked) ? 'active' : 'disabled'}`}
                    onClick={handleNext}
                    disabled={!(checkedItems.length > 0 && checkedItems.every(checked => checked))}
                >
                    가상 계좌 발급받기
                </button>
            </div>

            {/* Modal */}
            {showModal && (
                <>
                    <div className="modal-overlay" onClick={handleCloseModal}></div>
                    <div className="modal-container">
                        <div className="modal-icon">
                            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="32" cy="32" r="32" fill="#FEF2F2" />
                                <path d="M32 20C25.4 20 20 25.4 20 32C20 38.6 25.4 44 32 44C38.6 44 44 38.6 44 32C44 25.4 38.6 20 32 20ZM32 40C27.6 40 24 36.4 24 32C24 27.6 27.6 24 32 24C36.4 24 40 27.6 40 32C40 36.4 36.4 40 32 40Z" fill="#EF4444" />
                                <circle cx="32" cy="28" r="2" fill="#EF4444" />
                                <path d="M32 34C31.4 34 31 33.6 31 33V31C31 30.4 31.4 30 32 30C32.6 30 33 30.4 33 31V33C33 33.6 32.6 34 32 34Z" fill="#EF4444" />
                            </svg>
                        </div>
                        <h3 className="modal-title">내용을 모두 확인하셨나요?</h3>
                        <p className="modal-message">
                            보이스피싱이나 도박 자금 등 불법적인 목적으로 이용할 경우, 관련 법령에 따라 형사 처벌을 받습니다.
                        </p>
                                    <div className="modal-buttons">
                                        <button className="modal-confirm-button" onClick={handleConfirm} disabled={loading}>
                                            {loading ? (
                                                <span className="button-spinner">
                                                    <span className="spinner"></span>
                                                </span>
                                            ) : (
                                                '보이스피싱이나 도박 자금이 아닙니다.'
                                            )}
                                        </button>
                                        <button className="modal-close-button" onClick={handleCloseModal} disabled={loading}>
                                            닫기
                                        </button>
                                    </div>
                    </div>
                </>
            )}

            {/* 신고 모달 */}
            {showReportModal && (
                <>
                    <div className="modal-overlay" onClick={handleCloseReportModal}></div>
                    <div className="report-modal-container">
                        <div className="report-modal-header">
                            <h3 className="report-modal-title">신고하기</h3>
                            <button className="report-modal-close" onClick={handleCloseReportModal}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                        <div className="report-modal-content">
                            <div className="report-modal-item">
                                <img src="/img/police.svg" alt="경찰청" className="report-modal-icon" />
                                <div className="report-modal-info">
                                    <h4 className="report-modal-item-title">경찰청</h4>
                                    <p className="report-modal-item-desc">보이스피싱, 금융사기 등 범죄 신고</p>
                                </div>
                                <button className="report-modal-button" onClick={handlePoliceReport}>
                                    신고하기
                                </button>
                            </div>
                            <div className="report-modal-item">
                                <img src="/img/Financial.jpg" alt="금융감독원" className="report-modal-icon" />
                                <div className="report-modal-info">
                                    <h4 className="report-modal-item-title">금융감독원</h4>
                                    <p className="report-modal-item-desc">금융사기 피해 신고 및 상담</p>
                                </div>
                                <button className="report-modal-button" onClick={handleFinancialReport}>
                                    신고하기
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default VirtualAccountGuide;
